/**
 * 14-day paid-plan trial helpers (Standard / Teams).
 */

export const TRIAL_DAYS = 14;

export const isTrialingPlan = (planLike) => {
  if (!planLike) return false;
  return planLike.stripe_status === "trialing";
};

export const isTrialEligible = (planLike) => {
  return Boolean(planLike?.trial_eligible) && !isTrialingPlan(planLike);
};

export const isSmsAllowed = (planLike, featureAccess) => {
  if (featureAccess?.loaded && typeof featureAccess.smsAllowed === "boolean") {
    return featureAccess.smsAllowed;
  }
  if (featureAccess?.loaded && typeof featureAccess.sms_allowed === "boolean") {
    return featureAccess.sms_allowed;
  }
  if (typeof planLike?.sms_allowed === "boolean") {
    return planLike.sms_allowed;
  }
  return (
    planLike?.is_paid_subscription === "E" &&
    planLike?.stripe_status === "active"
  );
};

export const trialSourceFromState = ({ userPlan, featureAccess, user } = {}) => ({
  stripe_status:
    userPlan?.stripe_status ||
    featureAccess?.stripeStatus ||
    user?.stripe_status ||
    null,
  trial_ends_at:
    userPlan?.trial_ends_at ||
    featureAccess?.trialEndsAt ||
    user?.trial_ends_at ||
    null,
});

export const trialDaysRemaining = (trialEndsAt) => {
  if (!trialEndsAt) return 0;
  const end = new Date(trialEndsAt);
  if (Number.isNaN(end.getTime()) || end.getTime() <= Date.now()) {
    return 0;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);

  const days = Math.round(
    (endDay.getTime() - today.getTime()) / 86400000
  );
  // Stripe can land trial_end just past 14×24h (UTC midnight rounding),
  // which previously made Math.ceil show 15 on signup.
  return Math.max(0, Math.min(TRIAL_DAYS, days));
};

export const trialBannerText = (planLike) => {
  const endsAt = planLike?.trial_ends_at;
  if (!isTrialingPlan(planLike) && !endsAt) {
    return null;
  }
  if (planLike?.stripe_status && planLike.stripe_status !== "trialing") {
    return null;
  }
  const days = trialDaysRemaining(endsAt);
  if (!endsAt) {
    return null;
  }
  if (days <= 0) {
    return "Your trial ends today";
  }
  const dayLabel = days === 1 ? "day" : "days";
  return `Your trial ends in ${days} ${dayLabel}`;
};

export const trialPaymentCopy = (eligible) => {
  if (!eligible) {
    return {
      heading: "Complete your subscription",
      subheading: "Secure payment · Cancel anytime",
      submitLabel: (total) => `Subscribe · $${Number(total).toFixed(2)}`,
      dueToday: (total) => Number(total).toFixed(2),
      dueTodayLabel: "Total due today",
    };
  }

  return {
    heading: "Start your 14-day trial",
    subheading: "You won’t be charged today. We’ll charge you after 14 days unless you cancel.",
    submitLabel: () => "Start 14-day trial",
    dueToday: () => "0.00",
    dueTodayLabel: "Total due today",
  };
};
